import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestsAPI, spaceTypesAPI, storesAPI, inventoryAPI, artworksAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, Building2, Package, Upload, Image as ImageIcon, Search, AlertCircle, Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';

export default function CreateRequestPage() {
  const navigate = useNavigate();
  // Start at step 2 (Productos) since we removed the month selection step
  const [step, setStep] = useState(2);
  const [loading, setLoading] = useState(false);
  const [spaceTypes, setSpaceTypes] = useState([]);
  const [stores, setStores] = useState([]);
  const [requestId, setRequestId] = useState(null);
  const [inventoryData, setInventoryData] = useState({}); // {store_id: {space_type_id: availability}}
  
  // Form data - month will be set dynamically based on product allocations
  const [month, setMonth] = useState('');
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState([]);
  // allocations now stores: {spaceTypeId: {storeId: {quantity, startDate, endDate, startMonth, endMonth}}}
  const [allocations, setAllocations] = useState({});
  const [selectedStores, setSelectedStores] = useState([]);
  const [artworks, setArtworks] = useState({});
  
  // Calendar modal state
  const [calendarModal, setCalendarModal] = useState({ open: false, storeId: null, spaceTypeId: null });
  
  // Search and filter
  const [storeSearch, setStoreSearch] = useState('');
  const [regionFilter, setRegionFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [spacesRes, storesRes] = await Promise.all([
        spaceTypesAPI.getAll(),
        storesAPI.getAll()
      ]);
      setSpaceTypes(spacesRes.data);
      setStores(storesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  // Load inventory data when month changes
  const loadInventoryForMonth = async (selectedMonth) => {
    if (!selectedMonth) return;
    try {
      const response = await inventoryAPI.getSummary(selectedMonth);
      const invMap = {};
      response.data.forEach(item => {
        if (!invMap[item.store_id]) invMap[item.store_id] = {};
        invMap[item.store_id][item.space_type_id] = {
          capacity: item.capacity,
          reserved: item.reserved,
          holds: item.holds,
          available: item.available_quantity
        };
      });
      setInventoryData(invMap);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  // Create request when moving from step 2 to step 3
  const createRequestIfNeeded = async () => {
    if (requestId) return requestId; // Already created, return existing ID
    
    // Use current month as default requested_month (will be updated based on allocations)
    const defaultMonth = new Date().toISOString().slice(0, 7);
    setLoading(true);
    try {
      const response = await requestsAPI.create({ requested_month: defaultMonth, notes });
      const newRequestId = response.data.id;
      setRequestId(newRequestId);
      setMonth(defaultMonth);
      // Load inventory for current and next months
      await loadInventoryForMonth(defaultMonth);
      return newRequestId; // Return the new ID
    } catch (error) {
      toast.error('Error al crear solicitud');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { space_type_id: '', total_quantity: 1, product_name: '' }]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  // Check if all selected products are digital (don't need store selection)
  const areAllProductsDigital = () => {
    if (lineItems.length === 0) return false;
    return lineItems.every(item => {
      const product = spaceTypes.find(st => st.id === item.space_type_id);
      return product?.category === 'digital';
    });
  };

  const handleStep2Submit = async () => {
    if (lineItems.length === 0) {
      toast.error('Agrega al menos un espacio publicitario');
      return;
    }
    
    // Create request if not created yet - get the ID directly
    const currentRequestId = await createRequestIfNeeded();
    if (!currentRequestId) return;
    
    setLoading(true);
    try {
      for (const item of lineItems) {
        if (!item.space_type_id || item.total_quantity < 1) {
          toast.error('Completa todos los campos');
          setLoading(false);
          return;
        }
        await requestsAPI.addLineItem(currentRequestId, item);
      }
      
      // If all products are digital, skip store selection (step 3) and go directly to artworks (step 4)
      if (areAllProductsDigital()) {
        setStep(4);
        toast.success('Productos digitales agregados - No requieren asignación de tiendas');
      } else {
        setStep(3);
        toast.success('Espacios agregados');
      }
    } catch (error) {
      toast.error('Error al agregar espacios');
    } finally {
      setLoading(false);
    }
  };

  // Get availability for a store and space type
  const getAvailability = (storeId, spaceTypeId) => {
    return inventoryData[storeId]?.[spaceTypeId]?.available ?? 0;
  };

  // Check if store has any availability for the selected products
  const storeHasAvailability = (storeId) => {
    for (const item of lineItems) {
      const available = getAvailability(storeId, item.space_type_id);
      if (available > 0) return true;
    }
    return false;
  };

  // Get total availability for all selected products in a store
  const getStoreTotalAvailability = (storeId) => {
    let total = 0;
    for (const item of lineItems) {
      total += getAvailability(storeId, item.space_type_id);
    }
    return total;
  };

  const handleStoreSelection = (storeId, spaceTypeId = null) => {
    if (spaceTypeId) {
      const newAllocations = {...allocations};
      if (!newAllocations[spaceTypeId]) {
        newAllocations[spaceTypeId] = {};
      }
      
      if (newAllocations[spaceTypeId][storeId]) {
        // Deselect
        delete newAllocations[spaceTypeId][storeId];
      } else {
        // Select with default quantity but no period yet (user must select dates)
        const available = getAvailability(storeId, spaceTypeId);
        newAllocations[spaceTypeId][storeId] = {
          quantity: Math.min(1, available),
          startDate: '',
          endDate: '',
          startMonth: '',
          endMonth: ''
        };
      }
      setAllocations(newAllocations);
      
      // Update selectedStores based on all allocations
      const allStoreIds = new Set();
      Object.values(newAllocations).forEach(storeAllocs => {
        Object.keys(storeAllocs).forEach(sid => allStoreIds.add(sid));
      });
      setSelectedStores(Array.from(allStoreIds));
    }
  };

  const handleAllocationChange = (spaceTypeId, storeId, field, value) => {
    const current = allocations[spaceTypeId]?.[storeId] || { quantity: 0, startDate: '', endDate: '', startMonth: '', endMonth: '' };
    
    if (field === 'quantity') {
      const available = getAvailability(storeId, spaceTypeId);
      value = Math.min(Math.max(0, parseInt(value) || 0), available);
    }
    
    setAllocations({
      ...allocations,
      [spaceTypeId]: {
        ...allocations[spaceTypeId],
        [storeId]: {
          ...current,
          [field]: value
        }
      }
    });
  };

  const handlePeriodSelection = (spaceTypeId, storeId, selection) => {
    if (selection) {
      // Update all date fields at once
      const current = allocations[spaceTypeId]?.[storeId] || { quantity: 1 };
      setAllocations({
        ...allocations,
        [spaceTypeId]: {
          ...allocations[spaceTypeId],
          [storeId]: {
            ...current,
            startDate: selection.startDate,
            endDate: selection.endDate,
            startMonth: selection.startMonth,
            endMonth: selection.endMonth
          }
        }
      });
    }
    setCalendarModal({ open: false, storeId: null, spaceTypeId: null });
  };

  const openCalendarModal = (storeId, spaceTypeId) => {
    setCalendarModal({ open: true, storeId, spaceTypeId });
  };

  const getTotalAllocated = (spaceTypeId) => {
    if (!allocations[spaceTypeId]) return 0;
    return Object.values(allocations[spaceTypeId]).reduce((sum, alloc) => {
      const qty = typeof alloc === 'object' ? alloc.quantity : alloc;
      return sum + (qty || 0);
    }, 0);
  };

  const getRemainingQuantity = (spaceTypeId) => {
    const item = lineItems.find(i => i.space_type_id === spaceTypeId);
    if (!item) return 0;
    return item.total_quantity - getTotalAllocated(spaceTypeId);
  };

  const validateAllocations = () => {
    for (const item of lineItems) {
      const allocated = getTotalAllocated(item.space_type_id);
      if (allocated !== item.total_quantity) {
        toast.error(`Debes asignar exactamente ${item.total_quantity} unidades de ${spaceTypes.find(s => s.id === item.space_type_id)?.name}`);
        return false;
      }
      
      // Check that all allocations have periods selected (using dates now)
      const productAllocations = allocations[item.space_type_id] || {};
      for (const [storeId, alloc] of Object.entries(productAllocations)) {
        const allocData = typeof alloc === 'object' ? alloc : { quantity: alloc };
        if (!allocData.startDate || !allocData.endDate) {
          const store = stores.find(s => s.id === storeId);
          toast.error(`Selecciona el período de reserva para ${store?.name}`);
          return false;
        }
      }
    }
    return true;
  };

  const handleStep3Submit = () => {
    if (!validateAllocations()) {
      return;
    }
    setStep(4);
  };

  const handleArtworkFileChange = (spaceTypeId, files) => {
    setArtworks({
      ...artworks,
      [spaceTypeId]: {
        ...artworks[spaceTypeId],
        files: Array.from(files), // Store actual File objects
        fileNames: Array.from(files).map(f => f.name)
      }
    });
  };

  const handleArtworkNotesChange = (spaceTypeId, notes) => {
    setArtworks({
      ...artworks,
      [spaceTypeId]: {
        ...artworks[spaceTypeId],
        notes
      }
    });
  };

  const uploadArtworks = async () => {
    for (const [spaceTypeId, artworkData] of Object.entries(artworks)) {
      if (artworkData.files && artworkData.files.length > 0) {
        const uploadedUrls = [];
        
        // Upload each file
        for (const file of artworkData.files) {
          try {
            const response = await artworksAPI.uploadFile(file);
            uploadedUrls.push(response.data.url);
          } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(`Error al subir ${file.name}`);
          }
        }
        
        // Create artwork submission record
        if (uploadedUrls.length > 0) {
          try {
            await artworksAPI.create(requestId, spaceTypeId, uploadedUrls);
          } catch (error) {
            console.error('Error creating artwork record:', error);
          }
        }
      }
    }
  };

  const handleSubmitRequest = async (skipArtworks = false) => {
    setLoading(true);
    try {
      const requestDetail = await requestsAPI.getById(requestId);
      
      for (const lineItem of requestDetail.data.line_items) {
        const productAllocations = allocations[lineItem.space_type_id] || {};
        
        for (const [storeId, alloc] of Object.entries(productAllocations)) {
          const allocData = typeof alloc === 'object' ? alloc : { quantity: alloc, startMonth: month, endMonth: month };
          
          if (allocData.quantity > 0) {
            await requestsAPI.addAllocation(requestId, {
              line_item_id: lineItem.id,
              store_id: storeId,
              quantity: allocData.quantity,
              start_month: allocData.startMonth,
              end_month: allocData.endMonth
            });
          }
        }
      }

      // Upload artworks if not skipping
      if (!skipArtworks && Object.keys(artworks).length > 0) {
        toast.info('Subiendo artes...');
        await uploadArtworks();
      }

      await requestsAPI.submit(requestId);
      toast.success('Solicitud enviada exitosamente');
      navigate(`/requests/${requestId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setLoading(false);
    }
  };

  // Filter stores
  const regions = [...new Set(stores.map(s => s.region))];
  
  const filteredStores = stores.filter(store => {
    const matchesSearch = !storeSearch || 
      store.name.toLowerCase().includes(storeSearch.toLowerCase()) ||
      store.city.toLowerCase().includes(storeSearch.toLowerCase()) ||
      store.store_code.toLowerCase().includes(storeSearch.toLowerCase());
    
    const matchesRegion = regionFilter === 'all' || store.region === regionFilter;
    
    return matchesSearch && matchesRegion;
  });

  // Helper function to calculate months between two dates
  const calculateMonths = (start, end) => {
    if (!start || !end) return 0;
    const [startYear, startMonth] = start.split('-').map(Number);
    const [endYear, endMonth] = end.split('-').map(Number);
    return (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
  };

  // Get store and product names for calendar modal
  const getCalendarModalInfo = () => {
    if (!calendarModal.storeId || !calendarModal.spaceTypeId) return { storeName: '', productName: '' };
    const store = stores.find(s => s.id === calendarModal.storeId);
    const product = spaceTypes.find(s => s.id === calendarModal.spaceTypeId);
    const alloc = allocations[calendarModal.spaceTypeId]?.[calendarModal.storeId];
    const allocData = typeof alloc === 'object' ? alloc : {};
    return {
      storeName: store?.name || '',
      productName: product?.name || '',
      quantity: allocData.quantity || 1,
      startDate: allocData.startDate,
      endDate: allocData.endDate,
      startMonth: allocData.startMonth,
      endMonth: allocData.endMonth
    };
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="create-request-page">
      {/* Calendar Modal - Using DateRangePicker for day selection */}
      {calendarModal.open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">Seleccionar Período de Reserva</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setCalendarModal({ open: false, storeId: null, spaceTypeId: null })}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-6">
              <DateRangePicker
                storeName={getCalendarModalInfo().storeName}
                productName={getCalendarModalInfo().productName}
                quantity={getCalendarModalInfo().quantity}
                initialStartDate={getCalendarModalInfo().startDate}
                initialEndDate={getCalendarModalInfo().endDate}
                onSelectionChange={(selection) => {
                  if (selection) {
                    handlePeriodSelection(calendarModal.spaceTypeId, calendarModal.storeId, selection);
                  }
                }}
              />
              <div className="mt-4 flex justify-end gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setCalendarModal({ open: false, storeId: null, spaceTypeId: null })}
                >
                  Cancelar
                </Button>
                <Button 
                  onClick={() => setCalendarModal({ open: false, storeId: null, spaceTypeId: null })}
                  disabled={!getCalendarModalInfo().startDate}
                >
                  Confirmar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Progress Steps - Updated: Dynamically hide Distribution step for digital products */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {(areAllProductsDigital() ? [
            { num: 2, label: 'Productos' },
            { num: 4, label: 'Artes' }
          ] : [
            { num: 2, label: 'Productos' },
            { num: 3, label: 'Distribución' },
            { num: 4, label: 'Artes' }
          ]).map((s, index, arr) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s.num ? 'bg-primary text-white' : 'bg-slate-200 text-slate-600'
                }`}>
                  {step > s.num ? <Check className="w-5 h-5" /> : index + 1}
                </div>
                <span className={`text-xs mt-1 ${step >= s.num ? 'text-primary font-medium' : 'text-slate-500'}`}>
                  {s.label}
                </span>
              </div>
              {index < arr.length - 1 && (
                <div className={`flex-1 h-1 mx-2 ${step > s.num ? 'bg-primary' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Step 2: Product Selection - Now the first visible step */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Selecciona Productos</CardTitle>
            <CardDescription>Elige los espacios publicitarios y la cantidad que necesitas. El período se selecciona en el siguiente paso.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Notes field moved here */}
            <div className="mb-4">
              <Label htmlFor="notes">Notas de la campaña (opcional)</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Información adicional sobre la campaña..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            {lineItems.map((item, index) => {
              const selectedProduct = spaceTypes.find(st => st.id === item.space_type_id);
              return (
                <div key={index} className="p-4 border-2 border-slate-200 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">Producto {index + 1}</span>
                    <Button variant="ghost" size="sm" onClick={() => removeLineItem(index)}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Tipo de Espacio</Label>
                      <Select
                        value={item.space_type_id}
                        onValueChange={(val) => updateLineItem(index, 'space_type_id', val)}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Selecciona..." />
                        </SelectTrigger>
                        <SelectContent>
                          {spaceTypes.map((st) => (
                            <SelectItem key={st.id} value={st.id}>
                              {st.name} - ${st.base_price_usd}/mes
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Cantidad</Label>
                      <Input
                        type="number"
                        min="0"
                        value={item.total_quantity}
                        onChange={(e) => {
                          const val = e.target.value;
                          // Allow empty value while typing, then convert to number
                          if (val === '' || val === '0') {
                            updateLineItem(index, 'total_quantity', val === '' ? '' : 0);
                          } else {
                            updateLineItem(index, 'total_quantity', parseInt(val) || 0);
                          }
                        }}
                        onBlur={(e) => {
                          // Ensure minimum of 1 on blur if empty
                          if (e.target.value === '' || parseInt(e.target.value) < 1) {
                            updateLineItem(index, 'total_quantity', 1);
                          }
                        }}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {selectedProduct && (
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      {selectedProduct.image_url && (
                        <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-16 h-16 object-cover rounded" />
                      )}
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{selectedProduct.name}</p>
                        <p className="text-sm text-slate-600">{selectedProduct.description}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {selectedProduct.category === 'digital' ? 'Digital' : 'Presencial'}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            ${selectedProduct.base_price_usd}/mes
                          </Badge>
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Producto a Promocionar</Label>
                    <Input
                      value={item.product_name}
                      onChange={(e) => updateLineItem(index, 'product_name', e.target.value)}
                      placeholder="Nombre del producto a promocionar..."
                      className="mt-1"
                    />
                  </div>
                </div>
              );
            })}
            <Button variant="outline" onClick={addLineItem} className="w-full" data-testid="add-line-item">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Producto
            </Button>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleStep2Submit} disabled={loading} data-testid="step2-submit">
                {loading ? 'Procesando...' : 'Continuar'} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Store Selection with Allocation - IMPROVED */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Distribución por Tiendas</CardTitle>
            <CardDescription>Asigna cantidades específicas de cada producto por tienda</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Search and Filters */}
            <div className="flex gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar tienda por nombre, ciudad o código..."
                  value={storeSearch}
                  onChange={(e) => setStoreSearch(e.target.value)}
                  className="pl-10"
                  data-testid="store-search-input"
                />
              </div>
              <Select value={regionFilter} onValueChange={setRegionFilter}>
                <SelectTrigger className="w-48" data-testid="region-filter">
                  <SelectValue placeholder="Todas las regiones" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las regiones</SelectItem>
                  {regions.map(region => (
                    <SelectItem key={region} value={region}>{region}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Products allocation */}
            {lineItems.map((item) => {
              const product = spaceTypes.find(st => st.id === item.space_type_id);
              const remaining = getRemainingQuantity(item.space_type_id);
              
              // Filter stores that have availability for this product
              const availableStores = filteredStores.filter(store => {
                const available = getAvailability(store.id, item.space_type_id);
                return available > 0;
              });
              
              return (
                <div key={item.space_type_id} className="border-2 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b">
                    <div className="flex items-center gap-3">
                      {product?.image_url && (
                        <img src={product.image_url} alt={product.name} className="w-12 h-12 object-cover rounded" />
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-900">{product?.name}</h3>
                        <p className="text-sm text-slate-600">{item.product_name || 'Sin nombre'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-600">Total requerido: {item.total_quantity}</p>
                      <p className={`text-sm font-semibold ${remaining === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {remaining === 0 ? '✓ Completo' : `Faltan: ${remaining}`}
                      </p>
                    </div>
                  </div>

                  <div>
                    <Label className="mb-2 block">Tiendas con disponibilidad ({availableStores.length}):</Label>
                    
                    {availableStores.length === 0 ? (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <p className="text-sm text-amber-700">
                          No hay tiendas con disponibilidad para este producto. 
                          Por favor reduce la cantidad o intenta en otro momento.
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[500px] overflow-y-auto">
                        {availableStores.map((store) => {
                          const available = getAvailability(store.id, item.space_type_id);
                          const alloc = allocations[item.space_type_id]?.[store.id];
                          const allocData = typeof alloc === 'object' ? alloc : { quantity: alloc || 0, startDate: '', endDate: '', startMonth: '', endMonth: '' };
                          const isSelected = allocData.quantity > 0;
                          
                          return (
                            <div
                              key={store.id}
                              className={`p-3 border rounded-lg transition-all ${
                                isSelected ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleStoreSelection(store.id, item.space_type_id)}
                                  className="w-4 h-4 text-primary rounded"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-900 text-sm truncate">{store.name}</p>
                                  <p className="text-xs text-slate-500">{store.city}, {store.region}</p>
                                </div>
                                
                                {/* Availability Badge */}
                                <Badge 
                                  variant="outline" 
                                  className={`text-xs ${
                                    available <= 2 ? 'border-amber-300 text-amber-700 bg-amber-50' : 
                                    'border-emerald-300 text-emerald-700 bg-emerald-50'
                                  }`}
                                >
                                  Disp: {available}
                                </Badge>
                              </div>
                              
                              {isSelected && (
                                <div className="mt-3 pt-3 border-t border-slate-200 space-y-3">
                                  {/* Quantity selector */}
                                  <div className="flex items-center gap-3">
                                    <Label className="text-xs whitespace-nowrap w-20">Cantidad:</Label>
                                    <Input
                                      type="number"
                                      min="1"
                                      max={available}
                                      value={allocData.quantity}
                                      onChange={(e) => handleAllocationChange(item.space_type_id, store.id, 'quantity', e.target.value)}
                                      className="w-20 h-8 text-sm"
                                    />
                                    <span className="text-xs text-slate-500">/ {available} disponibles</span>
                                  </div>
                                  
                                  {/* Period selector - Date picker */}
                                  <div className="flex items-center gap-3">
                                    <Label className="text-xs whitespace-nowrap w-20">Período:</Label>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      className="flex-1 justify-start"
                                      onClick={() => openCalendarModal(store.id, item.space_type_id)}
                                    >
                                      <Calendar className="w-4 h-4 mr-2 text-primary" />
                                      {allocData.startDate && allocData.endDate ? (
                                        <span className="text-sm">
                                          {allocData.startDate} → {allocData.endDate}
                                        </span>
                                      ) : (
                                        <span className="text-slate-400">Seleccionar fecha inicio y fin</span>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(2)}>Atrás</Button>
              <Button onClick={handleStep3Submit} disabled={loading} data-testid="step3-submit">
                Continuar <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Upload Artworks (Optional) */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Cargar Artes (Opcional)</CardTitle>
            <CardDescription>
              Puedes subir las artes ahora o hacerlo después desde el detalle de la solicitud
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {lineItems.map((item, index) => {
              const spaceType = spaceTypes.find(st => st.id === item.space_type_id);
              if (!spaceType) return null;
              
              return (
                <div key={`${item.space_type_id}-${index}`} className="p-4 border-2 border-slate-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-4">
                    {spaceType.image_url && (
                      <img 
                        src={spaceType.image_url} 
                        alt={spaceType.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <h3 className="font-semibold text-slate-900">{spaceType.name}</h3>
                      <p className="text-sm text-slate-600">{item.product_name}</p>
                      {spaceType.art_requirements && (
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {spaceType.art_requirements.dimensions}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            {spaceType.art_requirements.formats_allowed?.join(', ')}
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <Label>Archivos de Arte</Label>
                      <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-primary transition-colors cursor-pointer">
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.jpg,.jpeg,.png,.ai,.psd"
                          onChange={(e) => handleArtworkFileChange(item.space_type_id, e.target.files)}
                          className="hidden"
                          id={`artwork-${item.space_type_id}`}
                        />
                        <label htmlFor={`artwork-${item.space_type_id}`} className="cursor-pointer">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">
                            Arrastra archivos aquí o haz clic para seleccionar
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            PDF, JPG, PNG, AI, PSD
                          </p>
                        </label>
                      </div>
                      {artworks[item.space_type_id]?.fileNames?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {artworks[item.space_type_id].fileNames.map((fileName, i) => (
                            <Badge key={i} variant="outline" className="flex items-center gap-1">
                              <ImageIcon className="w-3 h-3" />
                              {fileName}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div>
                      <Label>Notas para el Arte</Label>
                      <Textarea
                        placeholder="Instrucciones adicionales..."
                        value={artworks[item.space_type_id]?.notes || ''}
                        onChange={(e) => handleArtworkNotesChange(item.space_type_id, e.target.value)}
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setStep(areAllProductsDigital() ? 2 : 3)}>Atrás</Button>
              <Button variant="outline" onClick={() => handleSubmitRequest(true)}>
                Omitir y Enviar
              </Button>
              <Button onClick={() => handleSubmitRequest(false)} disabled={loading} data-testid="submit-request">
                {loading ? 'Enviando...' : 'Enviar Solicitud'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
